"use client"

import { Pressable } from "react-native"
import clsx from "clsx"
import { FormattedMessage, useIntl } from "react-intl"

import {
  appendReferralCode,
  Box,
  Button,
  H3,
  H4,
  Icon,
  isWeb,
  P,
  ScrollBox,
  Sheet,
  TextButton,
  useChannel,
  useExperienceContext,
  useExperienceMessageChannel,
  useExperienceUrl,
  useReferralCode,
} from "@bullseye/components"

import { AddToCalendarButton } from "../../AddToCalendarButton"
import { OpenActivityChatButton } from "../../OpenActivityChatButton"
import { ShareButton } from "../../ShareButton"
import { ActivityDate } from "../ActivityDate"
import { ActivityLocation } from "../ActivityLocation"
import { ActivityNoShowPolicy } from "../ActivityNoShowPolicy"
import { ActivityOrganizer } from "../ActivityOrganizer"

export type HouseRulesModalProps = {
  isOpen: boolean
  dismiss: VoidFunction
}

export const HouseRules = ({ isOpen, dismiss }: HouseRulesModalProps) => {
  const { experience } = useExperienceContext()
  const { messageChannel } = useExperienceMessageChannel(experience.id)
  const { channel, isLoading: isChannelLoading } = useChannel(
    messageChannel?.stream_channel_id,
  )
  const intl = useIntl()
  const baseUrl = useExperienceUrl(experience.slug)
  const { code: referralCode } = useReferralCode()
  const url = appendReferralCode(baseUrl, referralCode)
  const inviteMessage = intl.formatMessage(
    { id: "activity.share.message.preRsvp" },
    { title: experience.title, url },
  )
  const inviteSubject = intl.formatMessage(
    { id: "activity.share.subject" },
    { title: experience.title },
  )

  return (
    <Sheet
      icon="x"
      size="lg"
      show={isOpen}
      onIconPress={dismiss}
      dismiss={dismiss}
      renderContent={() => {
        return (
          <ScrollBox
            className="h-full md:pb-4"
            contentContainerStyle={{ flexGrow: 1 }}
          >
            <Pressable accessibilityRole="none">
              <Box className="flex g-6 md:pb-safe">
                <Box className="flex-col items-start g-2">
                  <P bold>
                    <FormattedMessage id="activity.attend.confirmModal.title" />
                  </P>
                  <H3>{experience.title}</H3>
                </Box>
                <Box
                  className={clsx(
                    "flex overflow-hidden rounded-lg bg-blue-100 p-4 g-3",
                    {
                      hidden: !experience.is_hostless,
                    },
                  )}
                >
                  <Box className="flex-row items-center g-2">
                    <Icon type="info-circle" size="lg" />
                    <H4>
                      <FormattedMessage id="activity.hostless" />
                    </H4>
                  </Box>
                  <P>
                    <FormattedMessage id="activity.hostless.modal.body.line1" />
                  </P>
                  <P>
                    <FormattedMessage id="activity.hostless.modal.body.line2" />
                  </P>
                  <Box className="inline">
                    <TextButton
                      as="a"
                      href="https://help.gethank.com/en/articles/8053635-how-to-find-other-hank-members-at-activities"
                      underlined
                    >
                      <FormattedMessage id="activity.hostless.modal.body.instructions" />
                    </TextButton>
                  </Box>
                </Box>
                <Box className="flex items-start px-4 g-6">
                  <ActivityOrganizer user={experience.organizer} />
                  <ActivityDate experience={experience} />
                  <ActivityLocation experience={experience} />
                  {experience.no_show_fee && (
                    <ActivityNoShowPolicy noShowFee={experience.no_show_fee} />
                  )}
                </Box>
              </Box>
            </Pressable>
          </ScrollBox>
        )
      }}
      renderFooter={() => {
        return (
          <Box className="flex flex-col gap-2 p-4">
            <Box className="flex flex-row gap-2">
              <Box className="flex-1">
                <OpenActivityChatButton
                  variant="primary"
                  outlined={true}
                  isEnabled
                  channelId={channel?.id}
                  loading={isChannelLoading}
                />
              </Box>
              {isWeb() && (
                <Box className="flex-1">
                  <AddToCalendarButton
                    outlined={true}
                    experienceSlug={experience.slug}
                  />
                </Box>
              )}
            </Box>
            <Box className="w-full">
              <ShareButton
                message={inviteMessage}
                url={url}
                emailSubject={inviteSubject}
                buttonType="button"
                variant="secondary"
                labelId="activity.share.inviteFriend"
                startIconType="plus"
                onPress={dismiss}
              />
            </Box>
            <Box className="w-full">
              <Button as="button" variant="primary" handlePress={dismiss}>
                <FormattedMessage id="general.close" />
              </Button>
            </Box>
          </Box>
        )
      }}
    />
  )
}
